/**
 * 动态表单模板 / 表单数据 API（对齐 FormController）。
 *
 * 真实端点（经网关 /api，StripPrefix=1）：
 * - GET  /form/templates/{id}          按 id 取表单模板（子步骤动态表单）
 * - GET  /form/templates/code/{code}   按 code 取表单模板
 * - GET  /form/data                    查订单在某节点的表单数据（回填）
 * - POST /form/data                    保存表单数据（按 orderId+nodeCode+substepCode+templateId 幂等 upsert）
 *
 * 子步骤动态表单链路：node_substeps.form_template_id → GET /form/templates/{id}
 * → 按 fields 渲染 → POST /form/data 落库 → 同时 PUT /container/substeps/records/{id}
 * 置 completed（data 一并带上前端保留一份）。
 */
import { request } from './request';
import type { FormTemplate, FormDataPayload } from './types';

export function getFormTemplateById(id: string): Promise<FormTemplate> {
  return request<FormTemplate>(`/form/templates/${id}`);
}

export function getFormTemplateByCode(code: string): Promise<FormTemplate> {
  return request<FormTemplate>(`/form/templates/code/${code}`);
}

/** 表单模板列表（按 category 可选筛选；建档弹窗按 code 找 container_archive 模板） */
export function listFormTemplates(category?: string): Promise<FormTemplate[]> {
  const params: Record<string, string> = {};
  if (category) params.category = category;
  return request<FormTemplate[]>('/form/templates', { params });
}

/** 表单数据（对齐 FormDataItem；data 为 JSON 字符串，与后台一致） */
export interface FormDataItem {
  id: string;
  orderId: string;
  nodeCode: string;
  substepCode?: string;
  templateId: string;
  /** JSON 字符串（后台用 JSON.stringify(formValues) 存储） */
  data: string;
  status?: string;
  submittedBy?: string;
  submittedAt?: string;
  createdAt?: string;
  [key: string]: unknown;
}

/** 查询订单在某节点的历史表单数据（编辑回填；无记录返回空数组） */
export function getFormData(orderId: string, nodeCode: string): Promise<FormDataItem[]> {
  return request<FormDataItem[]>('/form/data', { params: { orderId, nodeCode } });
}

/** 保存表单数据（幂等 upsert；data 传 JSON 字符串，与后台 saveFormData 口径一致） */
export function saveFormData(payload: {
  orderId: string;
  nodeCode: string;
  substepCode: string;
  templateId: string;
  data: string;
  status?: string;
}): Promise<FormDataItem> {
  return request<FormDataItem>('/form/data', { method: 'POST', body: payload });
}
